import { useEffect, useId, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { sl } from 'date-fns/locale';
import { format, isValid, parse } from 'date-fns';
import { CalendarDays, CircleAlert, X } from 'lucide-react';
import 'react-day-picker/style.css';

/** Pads to two digits: 9 -> "09". */
const two = (n: number) => String(n).padStart(2, '0');

/** `2026-05-01` <-> Date (local midnight). */
function parseIsoDate(value: string): Date | undefined {
  if (!value) return undefined;
  const d = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : undefined;
}

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
}

interface Props {
  label?: string;
  /** `yyyy-MM-dd` (as the old date input), or `yyyy-MM-ddTHH:mm` when `withTime`. Empty = none. */
  value: string;
  onChange: (value: string) => void;
  /** Adds a time (hours and minutes) next to the calendar; the value then has a `THH:mm` part. */
  withTime?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  id?: string;
}

/**
 * A date (or date-and-time) field with a calendar popover, in Slovenian. Replaces the browser's own date inputs,
 * which look different in every browser and age badly. Keeps the same string values as those inputs, so the pages
 * that used `type="date"` / `type="datetime-local"` need no other change.
 */
export function DateField({ label, value, onChange, withTime = false, disabled, error, placeholder, id }: Props) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const datePart = withTime ? value.slice(0, 10) : value;
  const timePart = withTime && value.length >= 16 ? value.slice(11, 16) : '';
  const selected = parseIsoDate(datePart);

  // Closes on a click outside or Escape, like a native picker.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function emit(date: Date | undefined, time: string) {
    if (!date) {
      onChange('');
      return;
    }
    onChange(withTime ? `${toIsoDate(date)}T${time || '00:00'}` : toIsoDate(date));
  }

  const display = selected
    ? withTime
      ? `${format(selected, 'd. M. yyyy', { locale: sl })} ${timePart || '00:00'}`
      : format(selected, 'd. M. yyyy', { locale: sl })
    : '';

  return (
    <div className="flex flex-col gap-1" ref={wrapperRef}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-fg">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          id={inputId}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={`flex h-10 w-full items-center gap-2 rounded-control border bg-surface-0 px-3 text-left text-sm transition-colors duration-fast disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-fg-muted ${
            error ? 'border-danger' : 'border-line-strong'
          } ${display ? 'text-fg' : 'text-fg-muted'}`}
        >
          <CalendarDays size={16} className="shrink-0 text-fg-muted" aria-hidden="true" />
          <span className="flex-1 truncate">{display || placeholder || (withTime ? 'Izberite datum in uro' : 'Izberite datum')}</span>
          {display && !disabled && (
            <span
              role="button"
              aria-label="Počisti"
              className="rounded-control p-0.5 text-fg-muted hover:bg-surface-2 hover:text-fg"
              onClick={(e) => {
                e.stopPropagation();
                emit(undefined, '');
                setOpen(false);
              }}
            >
              <X size={14} aria-hidden="true" />
            </span>
          )}
        </button>

        {open && (
          <div
            role="dialog"
            aria-label={label ?? 'Koledar'}
            className="absolute left-0 top-full z-30 mt-1 rounded-card border border-line bg-surface-1 p-3 shadow-card"
          >
            <DayPicker
              mode="single"
              locale={sl}
              weekStartsOn={1}
              showOutsideDays
              captionLayout="dropdown"
              startMonth={new Date(new Date().getFullYear() - 2, 0)}
              endMonth={new Date(new Date().getFullYear() + 5, 11)}
              selected={selected}
              defaultMonth={selected ?? new Date()}
              onSelect={(d) => {
                emit(d, timePart);
                if (!withTime) setOpen(false);
              }}
              className="aperture-daypicker"
            />
            {withTime && (
              <div className="mt-2 flex items-center justify-between gap-3 border-t border-line pt-3">
                <label className="flex items-center gap-2 text-sm text-fg-secondary">
                  Ura
                  <input
                    type="time"
                    value={timePart || '00:00'}
                    disabled={!selected}
                    onChange={(e) => emit(selected, e.target.value)}
                    className="h-9 rounded-control border border-line-strong bg-surface-0 px-2 text-sm text-fg"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="h-9 rounded-control bg-accent px-3 text-sm font-medium text-fg-on-accent hover:bg-accent-hover"
                >
                  V redu
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {error && (
        <span className="flex items-center gap-1 text-xs text-danger">
          <CircleAlert size={14} aria-hidden="true" />
          {error}
        </span>
      )}
    </div>
  );
}
